import React from 'react'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import type {OpiLiveScorecards, OpiAbandonedLanguage} from './_requests'
import {NotifyButton} from './NotifyButton'
import {OpiCardSkeleton} from './OpiCardSkeleton'

type Props = {
  scorecards: OpiLiveScorecards
  topAbandonedLanguages: OpiAbandonedLanguage[]
  /** Online Terps from Presence/SignalR */
  presenceReady?: boolean
  /** Success / Backstop / Abandoned from API */
  apiReady?: boolean
  onViewInterpreters: () => void
  onNotifyLanguage: (languageCode: string, languageName: string) => void
  notifyingCode: string | null
}

const fmt = (n: number) => n.toLocaleString()

export const OpiScorecards: React.FC<Props> = ({
  scorecards,
  topAbandonedLanguages,
  presenceReady = true,
  apiReady = true,
  onViewInterpreters,
  onNotifyLanguage,
  notifyingCode,
}) => {
  const totalOnline = scorecards.onCall + scorecards.available
  const onCallPct = totalOnline > 0 ? (scorecards.onCall / totalOnline) * 100 : 0
  const availPct = totalOnline > 0 ? (scorecards.available / totalOnline) * 100 : 0

  return (
    <div className='opi-scorecards'>
      {/* Socket-driven — show as soon as presence has seeded */}
      {!presenceReady ? (
        <OpiCardSkeleton variant='scorecard' />
      ) : (
        <div className='opi-scorecard opi-scorecard--online'>
          <div className='opi-scorecard__header'>
            <span className='opi-scorecard__label'>
              Online Terps
              <span className='opi-live-dot' title='Live via Presence' />
            </span>
            <img
              src={toAbsoluteUrl('/media/icons/opi/online-terps.svg')}
              alt=''
              className='opi-scorecard__icon'
            />
          </div>
          <div className='opi-scorecard__value'>{fmt(totalOnline)}</div>
          <div className='opi-scorecard__online-body'>
            <div className='opi-scorecard__bar-labels'>
              <span>On-call: {fmt(scorecards.onCall)}</span>
              <span>Available: {fmt(scorecards.available)}</span>
            </div>
            <div className='opi-scorecard__bar'>
              <div className='opi-scorecard__bar-oncall' style={{width: `${onCallPct}%`}} />
              <div className='opi-scorecard__bar-available' style={{width: `${availPct}%`}} />
            </div>
          </div>
          <div className='opi-scorecard__cta'>
            <button type='button' onClick={onViewInterpreters}>
              View Interpreters
            </button>
          </div>
        </div>
      )}

      {!apiReady ? (
        <OpiCardSkeleton variant='scorecard' />
      ) : (
        <div className='opi-scorecard opi-scorecard--success'>
          <div className='opi-scorecard__header'>
            <span className='opi-scorecard__label'>Internal Success Rate</span>
            <img
              src={toAbsoluteUrl('/media/icons/opi/success-rate.svg')}
              alt=''
              className='opi-scorecard__icon'
            />
          </div>
          <div className='opi-scorecard__value'>{scorecards.successRate}%</div>
          <div className='opi-scorecard__metrics opi-scorecard__metrics--bold'>
            <p>Total: {fmt(scorecards.totalCalls)}</p>
            <p>Connected: {fmt(scorecards.connectedCalls)}</p>
            <p>Abandoned: {fmt(scorecards.abandonedCalls)}</p>
          </div>
        </div>
      )}

      {!apiReady ? (
        <OpiCardSkeleton variant='scorecard' />
      ) : (
        <div className='opi-scorecard opi-scorecard--backstop'>
          <div className='opi-scorecard__header'>
            <span className='opi-scorecard__label'>Backstop Rate</span>
            <img
              src={toAbsoluteUrl('/media/icons/opi/backstop-rate.svg')}
              alt=''
              className='opi-scorecard__icon'
            />
          </div>
          <div className='opi-scorecard__backstop-summary'>
            <div className='opi-scorecard__metrics'>
              <p>Transferred: {fmt(scorecards.transferredTotal)}</p>
              <p className='bold'>Effectiff: {fmt(scorecards.effectiffCount)}</p>
              <p className='bold'>Bigword: {fmt(scorecards.bigwordCount)}</p>
            </div>
            <div className='opi-scorecard__value' style={{color: '#a3a242'}}>
              {scorecards.backstopRate}%
            </div>
          </div>
          <div className='opi-scorecard__section'>
            <span className='opi-scorecard__label'>Backstop Success</span>
            <div className='opi-scorecard__value opi-scorecard__value--centered'>
              {scorecards.backstopSuccessRate}%
            </div>
          </div>
        </div>
      )}

      {!apiReady ? (
        <OpiCardSkeleton variant='scorecard' />
      ) : (
        <div className='opi-scorecard opi-scorecard--abandoned'>
          <div className='opi-scorecard__header'>
            <span className='opi-scorecard__label'>Top 3 Abandoned Languages</span>
            <img
              src={toAbsoluteUrl('/media/icons/opi/abandoned-lang.svg')}
              alt=''
              className='opi-scorecard__icon'
            />
          </div>
          <div className='opi-scorecard__lang-list'>
            {topAbandonedLanguages.length === 0 ? (
              <div className='opi-empty' style={{padding: '8px 0'}}>
                No abandoned languages today
              </div>
            ) : (
              topAbandonedLanguages.map((lang) => (
                <div key={lang.languageCode} className='opi-scorecard__lang-row'>
                  <div className='opi-scorecard__lang-left'>
                    <span
                      className='opi-scorecard__lang-name'
                      title={`${lang.rank}. ${lang.languageName}`}
                    >
                      {lang.rank}. {lang.languageName}
                    </span>
                    <span className='opi-scorecard__lang-badge'>
                      {lang.abandonedCount} drops
                    </span>
                  </div>
                  <div className='opi-scorecard__lang-right'>
                    <span className='opi-scorecard__last-notified'>
                      Last Notified
                      <br />
                      {formatNotified(lang.lastNotified)}
                    </span>
                    <NotifyButton
                      loading={notifyingCode === lang.languageCode}
                      onClick={() => onNotifyLanguage(lang.languageCode, lang.languageName)}
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
function formatNotified(value: string | null): string {
  if (!value) return '—'
  // API already converted UTC → user profile timezone (AppointmentService pattern).
  // Format the wall-clock from the payload — do not apply another timezone.
  return formatApiWallClock(value)
}

function formatApiWallClock(value: string): string {
  const match = value.match(/T(\d{2}):(\d{2})/)
  if (!match) {
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return '—'
    const hours = d.getHours()
    const minutes = d.getMinutes()
    const h12 = hours % 12 || 12
    const ampm = hours >= 12 ? 'PM' : 'AM'
    return `${h12}:${String(minutes).padStart(2, '0')} ${ampm}`
  }
  const hours = parseInt(match[1], 10)
  const minutes = match[2]
  const h12 = hours % 12 || 12
  const ampm = hours >= 12 ? 'PM' : 'AM'
  return `${h12}:${minutes} ${ampm}`
}

